import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy, Eye, EyeOff, RefreshCw, Check, Shield, ShieldCheck, Network as NetIcon, Coins, KeyRound, Users as UsersIcon, Flame, Trash2, Plus, Send } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CoinIcon } from "@/components/common";

const FEE_COINS = ["USDT", "USDC", "BTC", "ETH", "BNB", "TRX", "SOL", "LTC"];

const COLORS = ["#94A3B8", "#1E3A5F", "#3B82F6", "#14B8A6", "#84CC16", "#2563EB",
  "#A855F7", "#EF4444", "#F87171", "#F59E0B", "#EAB308", "#92400E"];

export default function Settings() {
  const { t } = useLang();
  const { user, checkAuth } = useAuth();
  const [m, setM] = useState(null);
  const [showSecret, setShowSecret] = useState(false);
  const isAdmin = user?.role === "admin";

  useEffect(() => { api.get("/merchant").then((r) => setM(r.data.data)).catch(() => {}); }, []);
  if (!m) return <div className="text-slate-400">…</div>;

  const save = async () => {
    try {
      const { data } = await api.put("/merchant", {
        name: m.name, home_url: m.home_url, result_url: m.result_url,
        brand_color: m.brand_color, logo_url: m.logo_url, description: m.description,
        auto_swap: !!m.auto_swap, auto_swap_to: m.auto_swap_to || "USDT",
        auto_convert: m.auto_convert !== false,
        fees: m.fees || {}, allowed_ips: m.allowed_ips || [],
      });
      setM(data.data); toast.success(t("saved"));
    } catch (e) { toast.error(apiErr(e)); }
  };
  const regen = async () => {
    try { const { data } = await api.post("/merchant/regenerate"); setM(data.data); toast.success(t("regenerate")); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const testWebhook = async () => {
    if (!m.result_url) { toast.error("Спочатку вкажіть URL сповіщень та збережіть"); return; }
    try {
      const { data } = await api.post("/merchant/test-webhook");
      const d = data.data || {};
      if (d.delivered) toast.success(`Вебхук доставлено ✓ HTTP ${d.http_status}`);
      else toast.error(`Не доставлено: ${d.error || ("HTTP " + d.http_status)}`);
    } catch (e) { toast.error(apiErr(e)); }
  };
  const copy = (v) => { navigator.clipboard.writeText(v); toast.success(t("copied")); };

  const setFee = (iso, key, val) => {
    const fees = { ...(m.fees || {}) };
    fees[iso] = { ...(fees[iso] || {}), [key]: val === "" ? "" : Number(val) };
    setM({ ...m, fees });
  };
  const feeVal = (iso, key) => {
    const v = m.fees?.[iso]?.[key];
    return v === undefined || v === null ? "" : v;
  };

  return (
    <div className="space-y-6 oki-fade-up">
      <h1 className="text-2xl font-bold text-slate-900">{t("settings")}</h1>
      <div className="rounded-3xl bg-white p-6 shadow-sm border border-slate-100">
        <Tabs defaultValue="profile">
          <TabsList className="rounded-xl flex-wrap h-auto">
            <TabsTrigger value="profile" data-testid="tab-profile">{t("profile")}</TabsTrigger>
            <TabsTrigger value="merchant" data-testid="tab-merchant">{t("merchant")}</TabsTrigger>
            <TabsTrigger value="security" data-testid="tab-security"><Shield className="mr-1 h-4 w-4" />Безпека</TabsTrigger>
            {isAdmin && <TabsTrigger value="platform" data-testid="tab-platform" className="bg-emerald-50 data-[state=active]:bg-emerald-100"><Coins className="mr-1 h-4 w-4" />Платформа</TabsTrigger>}
            {isAdmin && <TabsTrigger value="networks" data-testid="tab-networks" className="bg-emerald-50 data-[state=active]:bg-emerald-100"><NetIcon className="mr-1 h-4 w-4" />Мережі</TabsTrigger>}
            {isAdmin && <TabsTrigger value="users" data-testid="tab-users" className="bg-emerald-50 data-[state=active]:bg-emerald-100"><UsersIcon className="mr-1 h-4 w-4" />Користувачі</TabsTrigger>}
            {isAdmin && <TabsTrigger value="hotwallet" data-testid="tab-hotwallet" className="bg-emerald-50 data-[state=active]:bg-emerald-100"><Flame className="mr-1 h-4 w-4" />Гарячий гаманець</TabsTrigger>}
          </TabsList>

          <TabsContent value="profile" className="pt-6">
            <div className="max-w-md space-y-4">
              <div><Label>{t("name")}</Label><Input value={user?.name || ""} disabled className="rounded-xl mt-1 bg-slate-50" /></div>
              <div><Label>{t("email")}</Label><Input value={user?.email || ""} disabled className="rounded-xl mt-1 bg-slate-50" /></div>
              <div className="text-xs text-slate-400">Провайдер входу: {user?.auth_provider || "password"}</div>
            </div>
          </TabsContent>

          <TabsContent value="merchant" className="pt-6">
            <div className="grid gap-8 lg:grid-cols-2">
              <div className="space-y-6">
                <div>
                  <div className="mb-3 text-lg font-bold text-slate-900">{t("merchant_info")}</div>
                  <div className="space-y-3">
                    <div><Label>{t("title")} *</Label><Input data-testid="merchant-name" value={m.name || ""} onChange={(e) => setM({ ...m, name: e.target.value })} className="rounded-xl mt-1" /></div>
                    <div><Label>{t("home_url")}</Label><Input data-testid="merchant-home" value={m.home_url || ""} onChange={(e) => setM({ ...m, home_url: e.target.value })} className="rounded-xl mt-1" placeholder="https://www.example.io" /></div>
                  </div>
                </div>
                <div>
                  <div className="mb-3 text-lg font-bold text-slate-900">{t("api_settings")}</div>
                  <div className="mb-2 text-xs text-slate-400">{t("api_key_note")}</div>
                  <div className="space-y-3">
                    <div><Label>{t("result_url")}</Label>
                      <Input data-testid="merchant-result-url" value={m.result_url || ""} onChange={(e) => setM({ ...m, result_url: e.target.value })} className="rounded-xl mt-1" placeholder="https://site.com/webhook" />
                      <div className="mt-2">
                        <Button size="sm" variant="outline" data-testid="test-webhook-btn" onClick={testWebhook} className="rounded-full border-slate-300">Надіслати тестовий вебхук</Button>
                        <p className="text-[11px] text-slate-400 mt-1">Надішле підписаний тестовий POST (заголовки token + sign) на цей URL, щоб перевірити прийом сповіщень.</p>
                      </div>
                    </div>
                    <div><Label>Дозволені IP (whitelist для API)</Label>
                      <Textarea
                        data-testid="merchant-allowed-ips"
                        value={(m.allowed_ips || []).join(", ")}
                        onChange={(e) => setM({ ...m, allowed_ips: e.target.value.split(/[\s,]+/).filter(Boolean) })}
                        className="rounded-xl mt-1 font-mono text-xs"
                        rows={2}
                        placeholder="72.60.34.27, 1.2.3.4"
                      />
                      <p className="text-[11px] text-slate-400 mt-1">
                        Якщо список не порожній — merchant API (створення інвойсів, виводи) прийматиме запити ТІЛЬКИ з цих IP-адрес. Розділяйте комою або пробілом. Порожньо = дозволені всі.
                      </p>
                    </div>
                    <div><Label>{t("token")}</Label>
                      <div className="mt-1 flex items-center gap-2 rounded-xl border border-slate-200 p-2">
                        <code data-testid="merchant-token" className="flex-1 truncate text-xs">{m.token}</code>
                        <Button size="sm" variant="ghost" data-testid="copy-token" onClick={() => copy(m.token)}><Copy className="mr-1 h-3.5 w-3.5" />{t("copy")}</Button>
                      </div>
                    </div>
                    <div><Label>{t("secret")}</Label>
                      <div className="mt-1 flex items-center gap-2 rounded-xl border border-slate-200 p-2">
                        <code data-testid="merchant-secret" className="flex-1 truncate text-xs">{showSecret ? m.secret : "•".repeat(40)}</code>
                        <Button size="icon" variant="ghost" data-testid="toggle-secret" onClick={() => setShowSecret(!showSecret)}>{showSecret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</Button>
                        <Button size="icon" variant="ghost" data-testid="copy-secret" onClick={() => copy(m.secret)}><Copy className="h-4 w-4" /></Button>
                      </div>
                    </div>
                    <Button data-testid="regen-btn" variant="outline" onClick={regen} className="rounded-full border-slate-300"><RefreshCw className="mr-2 h-4 w-4" />{t("regenerate")}</Button>
                  </div>
                </div>
              </div>

              <div>
                <div className="mb-3 text-lg font-bold text-slate-900">{t("branding")}</div>
                <Label>{t("color")}</Label>
                <div className="mt-2 flex flex-wrap gap-2">
                  {COLORS.map((c) => (
                    <button key={c} data-testid={`color-${c}`} onClick={() => setM({ ...m, brand_color: c })}
                      className="flex h-9 w-9 items-center justify-center rounded-full border-2 transition"
                      style={{ background: c, borderColor: m.brand_color === c ? "#0f172a" : "transparent" }}>
                      {m.brand_color === c && <Check className="h-4 w-4 text-white" />}
                    </button>
                  ))}
                </div>
                <div className="mt-6">
                  <Label>{t("logo")}</Label>
                  <div className="mt-2 flex items-center gap-3">
                    {m.logo_url ? (
                      <img src={m.logo_url} alt="logo" data-testid="merchant-logo-preview"
                        className="h-14 w-14 rounded-xl border border-slate-200 bg-white object-contain p-1" />
                    ) : (
                      <div className="flex h-14 w-14 items-center justify-center rounded-xl border border-dashed border-slate-300 text-xs text-slate-300">—</div>
                    )}
                    <div className="flex flex-col gap-1">
                      <label className="w-max cursor-pointer rounded-full bg-[#0D5C46] px-4 py-2 text-sm font-semibold text-white transition hover:bg-[#083C2E]">
                        {t("upload_logo")}
                        <input data-testid="merchant-logo-input" type="file" accept="image/*" className="hidden"
                          onChange={(e) => {
                            const f = e.target.files && e.target.files[0];
                            if (!f) return;
                            if (f.size > 512 * 1024) { toast.error("Файл завеликий (макс 512 КБ)"); return; }
                            const r = new FileReader();
                            r.onload = () => setM((prev) => ({ ...prev, logo_url: r.result }));
                            r.readAsDataURL(f);
                          }} />
                      </label>
                      {m.logo_url && (
                        <button type="button" data-testid="merchant-logo-remove" onClick={() => setM({ ...m, logo_url: "" })}
                          className="w-max text-xs text-rose-500 hover:underline">{t("remove")}</button>
                      )}
                      <span className="text-[11px] text-slate-400">{t("logo_hint")}</span>
                    </div>
                  </div>
                </div>
                <div className="mt-6"><Label>{t("description")}</Label>
                  <Textarea data-testid="merchant-desc" value={m.description || ""} onChange={(e) => setM({ ...m, description: e.target.value })} className="rounded-xl mt-1" rows={4} placeholder="Зробіть назву вашого бізнесу зрозумілою для клієнтів" /></div>

                <div className="mt-8">
                  <div className="mb-3 text-lg font-bold text-slate-900">{t("auto_conv")}</div>
                  <div className="flex items-start justify-between rounded-2xl border border-slate-100 p-4">
                    <div className="pr-4">
                      <div className="font-semibold text-slate-800">{t("auto_swap")}</div>
                      <div className="text-xs text-slate-400">{t("auto_swap_desc")}</div>
                    </div>
                    <Switch data-testid="auto-swap-toggle" checked={!!m.auto_swap} onCheckedChange={(v) => setM({ ...m, auto_swap: v })} />
                  </div>
                  {m.auto_swap && (
                    <div className="mt-3 max-w-[220px]"><Label>{t("convert_to")}</Label>
                      <Select value={m.auto_swap_to || "USDT"} onValueChange={(v) => setM({ ...m, auto_swap_to: v })}>
                        <SelectTrigger data-testid="auto-swap-to" className="rounded-xl mt-1"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-white border border-slate-200">{["USDT", "USDC", "ETH", "BNB"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  )}
                  <div className="mt-4 flex items-start justify-between rounded-2xl border border-slate-100 p-4">
                    <div className="pr-4">
                      <div className="font-semibold text-slate-800">Авто-конвертація при виведенні</div>
                      <div className="text-xs text-slate-400">Якщо на балансі бракує потрібної валюти (напр. BNB), платформа автоматично й реально обмінює її з USDT/USDC при виведенні через API або кабінет.</div>
                    </div>
                    <Switch data-testid="auto-convert-toggle" checked={m.auto_convert !== false} onCheckedChange={(v) => setM({ ...m, auto_convert: v })} />
                  </div>
                </div>
              </div>
            </div>
            <div className="mt-8 flex justify-end">
              <Button data-testid="save-merchant" onClick={save} className="rounded-full bg-blue-600 hover:bg-blue-700 px-8">{t("save")}</Button>
            </div>
          </TabsContent>

          <TabsContent value="security" className="pt-6">
            <SecurityTab onChanged={checkAuth} />
          </TabsContent>

          {isAdmin && (
            <TabsContent value="platform" className="pt-6">
              <PlatformTab user={user} />
            </TabsContent>
          )}
          {isAdmin && (
            <TabsContent value="networks" className="pt-6">
              <NetworksTab user={user} />
            </TabsContent>
          )}
          {isAdmin && (
            <TabsContent value="users" className="pt-6">
              <UsersTab admin={user} />
            </TabsContent>
          )}
          {isAdmin && (
            <TabsContent value="hotwallet" className="pt-6">
              <HotWalletTab user={user} />
            </TabsContent>
          )}
        </Tabs>
      </div>
    </div>
  );
}

// ---------------------- Security (2FA) Tab ----------------------
function SecurityTab({ onChanged }) {
  const { user } = useAuth();
  const [enabled, setEnabled] = useState(false);
  const [setupData, setSetupData] = useState(null);
  const [otp, setOtp] = useState("");
  const [disableOtp, setDisableOtp] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/security/2fa/status")
      .then((r) => setEnabled(!!r.data?.data?.enabled))
      .catch(() => {});
  }, []);

  const startSetup = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/security/2fa/setup");
      setSetupData(data.data);
    } catch (e) { toast.error(apiErr(e)); } finally { setLoading(false); }
  };
  const enable = async () => {
    try {
      await api.post("/security/2fa/enable", { otp });
      toast.success("2FA увімкнено!");
      setSetupData(null); setOtp(""); setEnabled(true);
      onChanged && onChanged();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const disable = async () => {
    try {
      await api.post("/security/2fa/disable", { otp: disableOtp });
      toast.success("2FA вимкнено");
      setDisableOtp(""); setEnabled(false);
      onChanged && onChanged();
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="max-w-2xl space-y-4">
      <div className="rounded-2xl border border-slate-100 p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className={"w-12 h-12 rounded-xl flex items-center justify-center " + (enabled ? "bg-emerald-100 text-emerald-600" : "bg-orange-100 text-orange-600")}>
            {enabled ? <ShieldCheck className="w-6 h-6" /> : <KeyRound className="w-6 h-6" />}
          </div>
          <div>
            <div className="text-lg font-bold text-slate-900">Google Authenticator (2FA)</div>
            <div className="text-sm text-slate-500">
              {enabled ? "Активна — вхід та критичні дії захищені кодом" : "Не увімкнена — рекомендуємо активувати"}
            </div>
          </div>
          {enabled ? <Badge className="ml-auto bg-emerald-600">УВІМКНЕНА</Badge> : <Badge variant="secondary" className="ml-auto">ВИМКНЕНА</Badge>}
        </div>
        <div className="text-sm text-slate-600 mb-4">
          Двофакторна автентифікація (TOTP) додає додатковий рівень безпеки: після пароля вам треба ввести 6-значний код з застосунку Google Authenticator (або Authy, 1Password).
        </div>

        {!enabled && !setupData && (
          <Button data-testid="2fa-start-btn" onClick={startSetup} disabled={loading}>{loading ? "Генерація…" : "Увімкнути 2FA"}</Button>
        )}

        {!enabled && setupData && (
          <div className="space-y-4">
            <div className="flex flex-col md:flex-row gap-6">
              <img src={setupData.qr_data_url} alt="QR" className="w-48 h-48 border rounded-lg bg-white" />
              <div className="flex-1 space-y-2 text-sm">
                <div className="font-semibold">Кроки:</div>
                <div>1. Відкрийте Google Authenticator</div>
                <div>2. Натисніть "+" → "Сканувати QR-код" → скануйте</div>
                <div>3. Або введіть секрет вручну:</div>
                <div className="font-mono text-xs bg-slate-100 p-2 rounded break-all select-all">{setupData.secret}</div>
                <div>4. Введіть 6-значний код з застосунку нижче:</div>
              </div>
            </div>
            <div className="flex gap-2">
              <Input data-testid="2fa-code" value={otp} onChange={(e) => setOtp(e.target.value)} placeholder="123 456" inputMode="numeric" maxLength={6} className="max-w-xs rounded-xl tracking-widest text-center" />
              <Button data-testid="2fa-enable-btn" onClick={enable}>Підтвердити і увімкнути</Button>
            </div>
          </div>
        )}

        {enabled && (
          <div className="space-y-3">
            <Label>Введіть поточний код для вимкнення 2FA</Label>
            <div className="flex gap-2">
              <Input data-testid="2fa-disable-code" value={disableOtp} onChange={(e) => setDisableOtp(e.target.value)} placeholder="6 цифр" inputMode="numeric" maxLength={6} className="max-w-xs rounded-xl tracking-widest text-center" />
              <Button data-testid="2fa-disable-btn" variant="destructive" onClick={disable}>Вимкнути 2FA</Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------- Platform Fees Tab (superadmin only) ----------------------
function PlatformTab({ user }) {
  const [pf, setPf] = useState(null);
  const [otp, setOtp] = useState("");
  const [treasury, setTreasury] = useState(null);
  const [labels, setLabels] = useState({});
  const [addrs, setAddrs] = useState({});
  const [pool, setPool] = useState([]);
  const [poolOtp, setPoolOtp] = useState("");
  const [cc, setCc] = useState(null);

  const loadAll = () => {
    api.get("/admin/platform-fees").then((r) => setPf(r.data.data)).catch((e) => toast.error(apiErr(e)));
    api.get("/admin/convert-config").then((r) => setCc(r.data.data)).catch(() => {});
    api.get("/admin/treasury-addresses").then((r) => {
      setTreasury(r.data.data);
      setLabels(r.data.data.labels);
      setAddrs(r.data.data.addresses || {});
    }).catch(() => {});
    api.get("/admin/pool").then((r) => setPool(r.data.data)).catch(() => {});
  };
  useEffect(() => { loadAll(); }, []);
  if (!pf || !treasury) return <div className="text-slate-400">…</div>;

  const save = async () => {
    try {
      const { data } = await api.put("/admin/platform-fees", {
        deposit_fee: Number(pf.deposit_fee),
        deposit_fee_by_iso: Object.fromEntries(
          Object.entries(pf.deposit_fee_by_iso || {}).map(([k, v]) => [k, Number(v)])
        ),
        swap_fee_by_iso: Object.fromEntries(
          Object.entries(pf.swap_fee_by_iso || {}).map(([k, v]) => [k, Number(v)])
        ),
        withdrawal_fee_cabinet: Number(pf.withdrawal_fee_cabinet),
        withdrawal_fee_api: Number(pf.withdrawal_fee_api),
        withdrawal_fee_by_key: Object.fromEntries(
          (pf.withdrawal_fee_matrix || []).map((m) => [m.key, { cabinet: Number(m.cabinet) || 0, api: Number(m.api) || 0 }])
        ),
        otp: otp || undefined,
      });
      setPf(data.data); setOtp("");
      toast.success("Комісії платформи оновлено");
    } catch (e) { toast.error(apiErr(e)); }
  };

  const saveTreasury = async () => {
    try {
      const { data } = await api.put("/admin/treasury-addresses", { addresses: addrs, otp: otp || undefined });
      setAddrs(data.data);
      toast.success("Treasury-адреси збережено");
    } catch (e) { toast.error(apiErr(e)); }
  };

  const withdrawPool = async (key) => {
    try {
      const { data } = await api.post("/admin/pool/withdraw", { key, otp: poolOtp || undefined });
      toast.success(`Заявку створено: ${data.data.amount} ${data.data.iso} → ${data.data.chain}`);
      setPoolOtp("");
      loadAll();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const saveConvert = async () => {
    try {
      const { data } = await api.put("/admin/convert-config", {
        auto_convert_enabled: !!cc.auto_convert_enabled,
        auto_convert_sources: cc.auto_convert_sources,
        otp: otp || undefined,
      });
      setCc(data.data);
      toast.success("Налаштування авто-конвертації збережено");
    } catch (e) { toast.error(apiErr(e)); }
  };

  const toggleConvSrc = (iso) => {
    const has = (cc.auto_convert_sources || []).includes(iso);
    const next = has ? cc.auto_convert_sources.filter((x) => x !== iso)
                     : [...(cc.auto_convert_sources || []), iso];
    setCc({ ...cc, auto_convert_sources: next });
  };

  return (
    <div className="space-y-5 max-w-4xl">
      <div className="rounded-2xl border border-emerald-100 bg-gradient-to-br from-emerald-50/60 to-transparent p-5">
        <div className="text-sm text-emerald-800 font-semibold mb-1">👑 Тільки для суперадміністратора FozPay</div>
        <div className="text-xs text-slate-600">Ці комісії застосовуються глобально до всіх мерчантів/користувачів платформи. Джерело доходу.</div>
      </div>

      {/* Auto-convert on withdrawal */}
      {cc && (
      <div className="rounded-2xl border border-blue-100 bg-gradient-to-br from-blue-50/60 to-transparent p-5 space-y-4" data-testid="convert-config-card">
        <div className="flex items-start justify-between">
          <div className="pr-4">
            <div className="text-lg font-bold text-slate-900">Авто-конвертація при виведенні</div>
            <div className="text-xs text-slate-500 mt-1">Коли мерчант виводить валюту (напр. BNB), якої бракує на балансі, платформа реально обмінює її з валюти-джерела через 1inch on-chain. Для opBNB своп виконується на BSC.</div>
          </div>
          <Switch data-testid="cc-enabled" checked={!!cc.auto_convert_enabled} onCheckedChange={(v) => setCc({ ...cc, auto_convert_enabled: v })} />
        </div>
        <div>
          <Label className="text-slate-700 font-semibold">Валюти-джерела для конвертації (у порядку пріоритету)</Label>
          <p className="text-xs text-slate-400 mt-1 mb-3">Платформа спробує кожну по черзі — перша, на якій вистачає балансу мерчанта, буде використана. Так адмін додає нові напрямки конвертації.</p>
          <div className="flex flex-wrap gap-2">
            {(cc.available_currencies || []).map((iso) => {
              const active = (cc.auto_convert_sources || []).includes(iso);
              return (
                <button key={iso} type="button" data-testid={`cc-src-${iso}`} onClick={() => toggleConvSrc(iso)}
                  className={`rounded-full px-4 py-1.5 text-sm font-semibold border transition-colors ${active ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-500 border-slate-200 hover:border-blue-300"}`}>
                  {iso}
                </button>
              );
            })}
          </div>
          <div className="mt-2 text-xs text-slate-500">Активні: <span className="font-mono">{(cc.auto_convert_sources || []).join(" → ") || "—"}</span></div>
        </div>
        <div className="flex justify-end">
          <Button data-testid="cc-save" onClick={saveConvert} className="rounded-full bg-blue-600 hover:bg-blue-700 px-6">Зберегти</Button>
        </div>
      </div>
      )}

      {/* Fees */}
      <div className="rounded-2xl border border-slate-100 p-5 space-y-4">
        <div className="text-lg font-bold text-slate-900">Комісії</div>
        <div>
          <Label className="text-slate-700 font-semibold">Комісія на вхід (депозит) — окремо для кожної валюти</Label>
          <p className="text-xs text-slate-400 mt-1 mb-3">Комісія списується з вхідної суми в одиницях самої валюти. Приклад: якщо для USDT задано 0.5 і відправник надіслав 10 USDT, одержувачу зараховується 9.5 USDT, а 0.5 йде в пул платформи.</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {Object.keys(pf.deposit_fee_by_iso || {}).map((iso) => (
              <div key={iso}>
                <Label className="text-xs font-semibold text-slate-600">{iso}</Label>
                <Input
                  data-testid={`pf-deposit-${iso}`}
                  type="number"
                  step="any"
                  min="0"
                  value={pf.deposit_fee_by_iso[iso]}
                  onChange={(e) => setPf({ ...pf, deposit_fee_by_iso: { ...pf.deposit_fee_by_iso, [iso]: e.target.value } })}
                  className="rounded-xl mt-1"
                />
              </div>
            ))}
          </div>
        </div>
        <div>
          <Label className="text-slate-700 font-semibold">Комісія при свопі (обміні) — окремо для кожної валюти, у %</Label>
          <p className="text-xs text-slate-400 mt-1 mb-3">Відсоток списується з валюти-джерела при обміні. Приклад: при 0.4% обмін 100 USDT → ETH утримає 0.4 USDT-еквіваленту комісії платформи.</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {Object.keys(pf.swap_fee_by_iso || {}).map((iso) => (
              <div key={iso}>
                <Label className="text-xs font-semibold text-slate-600">{iso} (%)</Label>
                <Input
                  data-testid={`pf-swap-${iso}`}
                  type="number"
                  step="any"
                  min="0"
                  max="100"
                  value={pf.swap_fee_by_iso[iso]}
                  onChange={(e) => setPf({ ...pf, swap_fee_by_iso: { ...pf.swap_fee_by_iso, [iso]: e.target.value } })}
                  className="rounded-xl mt-1"
                />
              </div>
            ))}
          </div>
        </div>
        <div>
          <Label className="text-slate-700 font-semibold">Комісія на вивід (з особистого кабінету)</Label>
          <Input data-testid="pf-wd-cab" type="number" step="0.01" value={pf.withdrawal_fee_cabinet} onChange={(e) => setPf({ ...pf, withdrawal_fee_cabinet: e.target.value })} className="rounded-xl mt-1 max-w-xs" />
        </div>
        <div>
          <Label className="text-slate-700 font-semibold">Комісія на вивід через API</Label>
          <Input data-testid="pf-wd-api" type="number" step="0.01" value={pf.withdrawal_fee_api} onChange={(e) => setPf({ ...pf, withdrawal_fee_api: e.target.value })} className="rounded-xl mt-1 max-w-xs" />
        </div>
        <div>
          <Label className="text-slate-700 font-semibold">Комісія на вивід — окремо для кожної валюти та мережі</Label>
          <p className="text-xs text-slate-400 mt-1 mb-3">Індивідуальна комісія на вивід для кожної пари «валюта + мережа». «Кабінет» — вивід із особистого кабінету, «API» — вивід через merchant API. Значення успадковують глобальні поля вище, доки не зміните їх тут.</p>
          <div className="oki-scroll overflow-x-auto rounded-xl border border-slate-100">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-xs text-slate-400 bg-slate-50">
                <th className="py-2 px-3">Валюта</th><th>Мережа</th><th>Комісія (кабінет)</th><th>Комісія (API)</th>
              </tr></thead>
              <tbody>
                {(pf.withdrawal_fee_matrix || []).map((m, i) => (
                  <tr key={m.key} data-testid={`pf-wd-row-${m.key}`} className="border-t border-slate-100">
                    <td className="py-2 px-3 font-semibold text-slate-700">{m.iso}</td>
                    <td className="text-slate-500">{m.network_name} ({m.network_iso})</td>
                    <td className="pr-2">
                      <Input data-testid={`pf-wd-cab-${m.key}`} type="number" step="any" min="0" value={m.cabinet}
                        onChange={(e) => { const mx = [...pf.withdrawal_fee_matrix]; mx[i] = { ...mx[i], cabinet: e.target.value }; setPf({ ...pf, withdrawal_fee_matrix: mx }); }}
                        className="h-9 w-28 rounded-lg" />
                    </td>
                    <td className="pr-2">
                      <Input data-testid={`pf-wd-api-${m.key}`} type="number" step="any" min="0" value={m.api}
                        onChange={(e) => { const mx = [...pf.withdrawal_fee_matrix]; mx[i] = { ...mx[i], api: e.target.value }; setPf({ ...pf, withdrawal_fee_matrix: mx }); }}
                        className="h-9 w-28 rounded-lg" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        {user?.two_fa?.enabled && (
          <div>
            <Label className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-emerald-600" /> Код 2FA</Label>
            <Input data-testid="pf-otp" value={otp} onChange={(e) => setOtp(e.target.value)} placeholder="123 456" className="rounded-xl mt-1 max-w-xs" inputMode="numeric" maxLength={6} />
          </div>
        )}
        <div className="flex justify-end">
          <Button data-testid="pf-save" onClick={save} className="rounded-full bg-emerald-600 hover:bg-emerald-700 px-8">Зберегти комісії</Button>
        </div>
      </div>

      {/* Treasury addresses per chain */}
      <div className="rounded-2xl border border-slate-100 p-5 space-y-4">
        <div>
          <div className="text-lg font-bold text-slate-900">🏦 Гаманці для отримання комісій (по мережах)</div>
          <div className="text-sm text-slate-500 mt-1">Комісії платформи в кожній мережі накопичуються та виводяться на відповідну вашу адресу.</div>
        </div>
        <div className="grid md:grid-cols-2 gap-3">
          {Object.entries(labels).map(([chain, label]) => (
            <div key={chain} className="rounded-xl bg-slate-50 border border-slate-100 p-3">
              <Label className="text-xs text-slate-500">{label}</Label>
              <Input
                data-testid={`treasury-${chain}`}
                value={addrs[chain] || ""}
                onChange={(e) => setAddrs({ ...addrs, [chain]: e.target.value })}
                placeholder={chain === "tron" ? "T..." : chain === "bitcoin" ? "bc1... / 1... / 3..." : chain === "litecoin" ? "ltc1... / L... / M..." : chain === "solana" ? "Solana address" : "0x..."}
                className="rounded-lg mt-1 font-mono text-xs"
              />
            </div>
          ))}
        </div>
        <div className="flex justify-end">
          <Button data-testid="treasury-save" onClick={saveTreasury} className="rounded-full">Зберегти адреси</Button>
        </div>
      </div>

      {/* Pool */}
      <div className="rounded-2xl border border-emerald-100 p-5 space-y-3">
        <div className="flex items-start justify-between">
          <div>
            <div className="text-lg font-bold text-slate-900">💵 Пул платформи (доступно до виводу)</div>
            <div className="text-sm text-slate-500 mt-1">Комісії, накопичені по кожній валюті та мережі. Натисніть "Вивести" щоб надіслати на відповідну treasury-адресу.</div>
          </div>
          {user?.two_fa?.enabled && pool.length > 0 && (
            <div className="max-w-[160px]">
              <Label className="text-xs">2FA код</Label>
              <Input data-testid="pool-otp" value={poolOtp} onChange={(e) => setPoolOtp(e.target.value)} placeholder="123 456" className="rounded-lg mt-1" inputMode="numeric" maxLength={6} />
            </div>
          )}
        </div>
        {pool.length === 0 ? (
          <div className="text-sm text-slate-400 py-4">Ще нічого не накопичено. Комісії з'являться після перших транзакцій.</div>
        ) : (
          <div className="space-y-2">
            {pool.map((row) => (
              <div key={row.key} className="flex items-center justify-between rounded-xl border border-slate-100 p-3 hover:bg-slate-50">
                <div className="flex items-center gap-3">
                  <CoinIcon iso={row.iso} size={30} />
                  <div>
                    <div className="font-semibold text-slate-900">{row.amount.toFixed(6)} {row.iso}</div>
                    <div className="text-xs text-slate-500">{row.network_name} · {row.chain}</div>
                    {row.treasury_address ? (
                      <div className="text-[10px] text-slate-400 font-mono mt-1">→ {row.treasury_address}</div>
                    ) : (
                      <div className="text-[11px] text-orange-600 mt-1">⚠️ Не задано treasury для {row.chain}</div>
                    )}
                  </div>
                </div>
                <Button
                  data-testid={`pool-wd-${row.key}`}
                  onClick={() => withdrawPool(row.key)}
                  disabled={!row.withdrawable}
                  className="rounded-full bg-emerald-600 hover:bg-emerald-700"
                >
                  Вивести
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------- Networks Tab (superadmin only) ----------------------
function NetworksTab({ user }) {
  const [nets, setNets] = useState([]);
  const [otp, setOtp] = useState("");
  const load = () => api.get("/admin/networks").then((r) => setNets(r.data.data)).catch((e) => toast.error(apiErr(e)));
  const [confDraft, setConfDraft] = useState({});
  const saveConf = async (n) => {
    const v = confDraft[n.network_id];
    if (v === undefined || v === "") return;
    try {
      await api.put("/admin/networks/confirmations", { network_id: n.network_id, confirmations: Number(v), otp: otp || undefined });
      toast.success(`${n.name}: ${v} підтверджень`);
      setConfDraft((d) => ({ ...d, [n.network_id]: undefined }));
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };
  useEffect(() => { load(); }, []);
  const toggle = async (n, val) => {
    try {
      await api.put("/admin/networks", { network_id: n.network_id, enabled: val, otp: otp || undefined });
      toast.success(`${n.name}: ${val ? "увімкнено" : "вимкнено"}`);
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <div className="space-y-4 max-w-3xl">
      <div className="rounded-2xl border border-emerald-100 bg-gradient-to-br from-emerald-50/60 to-transparent p-5">
        <div className="text-sm text-emerald-800 font-semibold mb-1">👑 Керування платіжними мережами</div>
        <div className="text-xs text-slate-600">Вимкнення тумблера миттєво зупиняє прийом та вивід коштів у відповідній мережі на всій платформі.</div>
      </div>
      {user?.two_fa?.enabled && (
        <div className="rounded-2xl border border-slate-100 p-3">
          <Label className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-emerald-600" /> Код 2FA для змін</Label>
          <Input data-testid="net-otp" value={otp} onChange={(e) => setOtp(e.target.value)} placeholder="123 456" className="rounded-xl mt-1 max-w-xs" inputMode="numeric" maxLength={6} />
        </div>
      )}
      <div className="space-y-2">
        {nets.map((n) => (
          <div key={n.network_id} data-testid={`net-row-${n.network_id}`} className="flex items-center justify-between rounded-2xl border border-slate-100 p-4 hover:bg-slate-50">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-xl bg-slate-100 flex items-center justify-center text-xs font-bold text-slate-700">{n.iso}</div>
              <div>
                <div className="font-semibold text-slate-900">{n.name}</div>
                <div className="text-xs text-slate-500 capitalize">{n.chain}</div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1" title="Скільки підтверджень мережі потрібно для зарахування депозиту">
                <Input data-testid={`net-conf-${n.network_id}`} type="number" min="0"
                  value={confDraft[n.network_id] ?? n.confirmations}
                  onChange={(e) => setConfDraft((d) => ({ ...d, [n.network_id]: e.target.value }))}
                  className="h-8 w-16 rounded-lg text-center" />
                <span className="text-xs text-slate-400">підтв.</span>
                <Button size="sm" variant="outline" data-testid={`net-conf-save-${n.network_id}`} onClick={() => saveConf(n)} className="h-8 rounded-lg px-2 text-xs">OK</Button>
              </div>
              <Badge variant={n.enabled ? "default" : "secondary"} className={n.enabled ? "bg-emerald-600" : ""}>{n.enabled ? "Активна" : "Вимкнена"}</Badge>
              <Switch data-testid={`net-toggle-${n.network_id}`} checked={!!n.enabled} onCheckedChange={(v) => toggle(n, v)} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------------- Users management (admin only) ----------------------
function UsersTab({ admin }) {
  const [users, setUsers] = useState([]);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [pwEdit, setPwEdit] = useState({});
  const [balEdit, setBalEdit] = useState({});   // uid -> {iso, amount}
  const [regEnabled, setRegEnabled] = useState(false);
  const load = () => api.get("/admin/users").then((r) => setUsers(r.data.data)).catch((e) => toast.error(apiErr(e)));
  const loadReg = () => api.get("/admin/registration").then((r) => setRegEnabled(!!r.data.data.enabled)).catch(() => {});
  const toggleReg = async (v) => {
    try {
      const { data } = await api.put("/admin/registration", { enabled: v });
      setRegEnabled(!!data.data.enabled);
      toast.success(v ? "Реєстрацію клієнтів увімкнено" : "Реєстрацію клієнтів вимкнено");
    } catch (e) { toast.error(apiErr(e)); }
  };
  useEffect(() => { load(); loadReg(); }, []);

  const create = async () => {
    try {
      await api.post("/admin/users", { email, name, password });
      toast.success("Користувача створено (активний)");
      setEmail(""); setName(""); setPassword(""); load();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const changePw = async (uid) => {
    const pw = pwEdit[uid];
    if (!pw) return;
    try {
      await api.put("/admin/users/password", { user_id: uid, password: pw });
      toast.success("Пароль оновлено");
      setPwEdit({ ...pwEdit, [uid]: "" });
    } catch (e) { toast.error(apiErr(e)); }
  };
  const del = async (uid) => {
    try { await api.delete(`/admin/users/${uid}`); toast.success("Видалено"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const setStatus = async (uid, status) => {
    try {
      await api.put(`/admin/users/${uid}/status`, { status });
      toast.success(status === "active" ? "Клієнта активовано — доступ відкрито" : "Доступ клієнта призупинено");
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const saveBalance = async (uid) => {
    const row = balEdit[uid] || {};
    if (!row.iso || row.amount === "" || row.amount == null) return;
    try {
      await api.put(`/admin/users/${uid}/balance`, { iso: row.iso, balance: Number(row.amount) });
      toast.success(`Баланс ${row.iso} оновлено`);
      setBalEdit({ ...balEdit, [uid]: { iso: row.iso, amount: "" } });
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const pendingCount = users.filter((u) => u.status === "pending").length;

  return (
    <div className="space-y-5 max-w-4xl">
      <div className="rounded-2xl border border-emerald-100 bg-gradient-to-br from-emerald-50/70 to-transparent p-5">
        <div className="text-sm text-emerald-800 font-semibold mb-1">👥 Керування користувачами</div>
        <div className="text-xs text-slate-600">Створюйте акаунти, підтверджуйте нових клієнтів, змінюйте паролі та баланси по кожній валюті.</div>
      </div>

      <div data-testid="registration-toggle-card" className="flex items-center justify-between gap-4 rounded-2xl border border-emerald-100 bg-gradient-to-br from-emerald-50/70 to-transparent p-5">
        <div className="min-w-0">
          <div className="font-semibold text-slate-900">Реєстрація клієнтів</div>
          <div className="mt-0.5 text-xs text-slate-600">Дозволити публічну реєстрацію (email + Google). <b>Кожен новий клієнт потрапляє у статус «очікує» і отримує доступ лише після вашого підтвердження нижче</b> — навіть після реєстрації через Google.</div>
          <div className={`mt-1 text-xs font-semibold ${regEnabled ? "text-emerald-700" : "text-slate-500"}`}>
            {regEnabled ? "● Реєстрація УВІМКНЕНА" : "○ Реєстрація ВИМКНЕНА"}
          </div>
        </div>
        <Switch data-testid="registration-switch" checked={regEnabled} onCheckedChange={toggleReg} />
      </div>

      {pendingCount > 0 && (
        <div data-testid="pending-count" className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
          ⏳ Очікують підтвердження: <b>{pendingCount}</b> — активуйте їх у списку нижче.
        </div>
      )}

      <div className="rounded-2xl border border-slate-100 p-5">
        <div className="text-lg font-bold text-slate-900 mb-3">Створити нового користувача</div>
        <div className="grid gap-3 sm:grid-cols-4">
          <div><Label>Ім'я</Label><Input data-testid="new-user-name" value={name} onChange={(e) => setName(e.target.value)} className="rounded-xl mt-1" placeholder="Ivan" /></div>
          <div><Label>Email</Label><Input data-testid="new-user-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="rounded-xl mt-1" placeholder="user@mail.com" /></div>
          <div><Label>Пароль</Label><Input data-testid="new-user-password" value={password} onChange={(e) => setPassword(e.target.value)} className="rounded-xl mt-1" placeholder="••••••" /></div>
          <div className="flex items-end"><Button data-testid="create-user-btn" onClick={create} disabled={!email || !password} className="w-full rounded-full bg-emerald-600 hover:bg-emerald-700"><Plus className="mr-1 h-4 w-4" />Створити</Button></div>
        </div>
      </div>

      <div className="rounded-2xl border border-slate-100 p-5">
        <div className="text-lg font-bold text-slate-900 mb-3">Усі користувачі ({users.length})</div>
        <div className="space-y-3">
          {users.map((u) => {
            const isPending = u.status === "pending" && u.role !== "admin";
            const be = balEdit[u.user_id] || { iso: "USDT", amount: "" };
            return (
              <div key={u.user_id} data-testid={`user-row-${u.user_id}`} className={`rounded-xl border p-3 ${isPending ? "border-amber-200 bg-amber-50/40" : "border-slate-100 hover:bg-white/50"}`}>
                <div className="flex flex-wrap items-center gap-3">
                  <div className="flex-1 min-w-[180px]">
                    <div className="font-semibold text-slate-900 flex items-center gap-2">
                      {u.name || u.email}
                      {u.role === "admin" && <Badge className="bg-emerald-600">admin</Badge>}
                      {u.role !== "admin" && (
                        <Badge data-testid={`user-status-${u.user_id}`} className={isPending ? "bg-amber-500" : "bg-emerald-600"}>
                          {isPending ? "очікує" : "активний"}
                        </Badge>
                      )}
                    </div>
                    <div className="text-xs text-slate-500">{u.email} · {u.auth_provider || "password"}</div>
                    <div data-testid={`user-balance-${u.user_id}`} className="mt-1 text-xs font-semibold text-emerald-700">
                      Баланс: ${Number(u.balance_usdt ?? 0).toFixed(2)}
                      {u.balances && u.balances.length > 0 && (
                        <span className="ml-2 font-normal text-slate-400">{u.balances.map((b) => `${b.balance} ${b.iso}`).join(" · ")}</span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {u.role !== "admin" && (
                      isPending ? (
                        <Button data-testid={`user-activate-${u.user_id}`} size="sm" onClick={() => setStatus(u.user_id, "active")} className="rounded-full bg-emerald-600 hover:bg-emerald-700">✓ Активувати</Button>
                      ) : (
                        <Button data-testid={`user-suspend-${u.user_id}`} size="sm" variant="outline" onClick={() => setStatus(u.user_id, "pending")} className="rounded-full">Призупинити</Button>
                      )
                    )}
                    <Input data-testid={`pw-input-${u.user_id}`} value={pwEdit[u.user_id] || ""} onChange={(e) => setPwEdit({ ...pwEdit, [u.user_id]: e.target.value })} placeholder="Новий пароль" className="h-9 w-36 rounded-lg" />
                    <Button data-testid={`pw-save-${u.user_id}`} size="sm" onClick={() => changePw(u.user_id)} disabled={!pwEdit[u.user_id]} className="rounded-full bg-emerald-600 hover:bg-emerald-700"><KeyRound className="mr-1 h-3.5 w-3.5" />Пароль</Button>
                    {u.user_id !== admin?.user_id && (
                      <Button data-testid={`user-del-${u.user_id}`} size="icon" variant="ghost" onClick={() => del(u.user_id)} className="text-rose-500"><Trash2 className="h-4 w-4" /></Button>
                    )}
                  </div>
                </div>
                {/* balance editor per currency */}
                {u.role !== "admin" && (
                  <div className="mt-3 flex flex-wrap items-end gap-2 rounded-lg bg-slate-50 p-3">
                    <div className="text-xs font-semibold text-slate-600 self-center mr-1">Змінити баланс:</div>
                    <Select value={be.iso} onValueChange={(v) => setBalEdit({ ...balEdit, [u.user_id]: { ...be, iso: v } })}>
                      <SelectTrigger data-testid={`bal-iso-${u.user_id}`} className="h-9 w-28 rounded-lg"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-white border border-slate-200">
                        {FEE_COINS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Input data-testid={`bal-amount-${u.user_id}`} type="number" step="any" value={be.amount}
                      onChange={(e) => setBalEdit({ ...balEdit, [u.user_id]: { ...be, amount: e.target.value } })}
                      placeholder="Нове значення" className="h-9 w-40 rounded-lg" />
                    <Button data-testid={`bal-save-${u.user_id}`} size="sm" onClick={() => saveBalance(u.user_id)}
                      disabled={be.amount === "" || be.amount == null} className="rounded-full bg-[#0D5C46] hover:bg-[#083C2E]">Зберегти</Button>
                    <span className="text-[11px] text-slate-400 self-center">встановлює абсолютне значення балансу по вибраній валюті</span>
                  </div>
                )}
              </div>
            );
          })}
          {users.length === 0 && <div className="py-6 text-center text-sm text-slate-400">Ще немає користувачів</div>}
        </div>
      </div>
    </div>
  );
}

// ---------------------- Hot wallet (admin only) ----------------------
function HotWalletTab({ user }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ chain: "ethereum", iso: "USDT", to_address: "", amount: "", otp: "" });
  const [busy, setBusy] = useState(false);
  const [seed, setSeed] = useState(null);
  const [seedForm, setSeedForm] = useState({ mnemonic: "", otp: "", open: false });
  const [seedBusy, setSeedBusy] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/admin/hot-wallet")
      .then((r) => setData(r.data.data))
      .catch((e) => toast.error(apiErr(e)))
      .finally(() => setLoading(false));
  };
  const loadSeed = () => {
    api.get("/admin/hot-wallet/seed-status")
      .then((r) => setSeed(r.data.data))
      .catch(() => {});
  };
  useEffect(() => { load(); loadSeed(); }, []);

  const saveSeed = async () => {
    setSeedBusy(true);
    try {
      await api.post("/admin/hot-wallet/set-mnemonic", {
        mnemonic: seedForm.mnemonic, otp: seedForm.otp || undefined,
      });
      toast.success("Сід збережено у зашифрованому вигляді");
      setSeedForm({ mnemonic: "", otp: "", open: false });
      loadSeed(); load();
    } catch (e) { toast.error(apiErr(e)); } finally { setSeedBusy(false); }
  };

  const withdraw = async () => {
    setBusy(true);
    try {
      const { data: res } = await api.post("/admin/hot-wallet/withdraw", {
        chain: form.chain, iso: form.iso, to_address: form.to_address,
        amount: Number(form.amount), otp: form.otp || undefined,
      });
      toast.success(`Відправлено! TX: ${res.data.tx_hash.slice(0, 14)}…`);
      setForm({ ...form, amount: "", to_address: "" });
      setTimeout(load, 3000);
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const copy = (v) => { navigator.clipboard.writeText(v); toast.success("Скопійовано"); };

  return (
    <div className="space-y-5 max-w-4xl">
      <div className="rounded-2xl border border-emerald-100 bg-gradient-to-br from-emerald-50/70 via-emerald-50/50 to-transparent p-5">
        <div className="flex items-center gap-2 text-sm text-emerald-800 font-semibold mb-1"><Flame className="h-4 w-4" /> Гарячий гаманець платформи</div>
        <div className="text-xs text-slate-600">Усі депозити автоматично зводяться сюди. Звідси проводяться виводи та своп. Комісії сервісу теж лишаються тут — ви можете вивести будь-яку валюту на потрібну адресу.</div>
      </div>

      <div className="rounded-2xl border border-emerald-100 bg-gradient-to-br from-emerald-50/70 to-transparent p-5 space-y-3" data-testid="seed-security-card">
        <div className="flex items-center gap-2 text-sm text-emerald-800 font-semibold"><ShieldCheck className="h-4 w-4" /> Безпека сід-фрази</div>
        <div className="text-xs text-slate-600">
          Сід гарячого гаманця зберігається <b>зашифрованим</b> у базі (AES/Fernet). Ключ розшифрування (<code>MNEMONIC_ENC_KEY</code>) лежить лише в <code>.env</code> на сервері — навіть з дампом БД сід не прочитати. Відкритим текстом сід ніде не зберігається й не показується.
        </div>
        {seed && (
          <div className="flex flex-wrap gap-2 text-xs">
            <span className="rounded-full bg-emerald-100 text-emerald-800 px-3 py-1">Зашифровано в БД ✓</span>
            {seed.auto_generated && <span className="rounded-full bg-amber-100 text-amber-800 px-3 py-1" data-testid="seed-auto-warn">⚠ Згенеровано системою — замініть своїм</span>}
            {seed.env_override && <span className="rounded-full bg-red-100 text-red-800 px-3 py-1">⚠ Сід заданий у .env (небезпечно)</span>}
          </div>
        )}
        {!seedForm.open ? (
          <Button data-testid="seed-set-open" size="sm" variant="outline" className="rounded-full"
            onClick={() => setSeedForm({ ...seedForm, open: true })} disabled={seed?.env_override}>
            {seed?.auto_generated ? "Ввести свою сід-фразу" : "Замінити сід-фразу"}
          </Button>
        ) : (
          <div className="space-y-3 rounded-xl border border-emerald-100 bg-white p-4">
            <div>
              <Label>Сід-фраза (12/24 слова, BIP-39)</Label>
              <textarea data-testid="seed-input" value={seedForm.mnemonic}
                onChange={(e) => setSeedForm({ ...seedForm, mnemonic: e.target.value })}
                rows={3} autoComplete="off" spellCheck={false}
                className="mt-1 w-full rounded-xl border border-slate-200 p-3 font-mono text-xs"
                placeholder="word1 word2 word3 …" />
              <div className="mt-1 text-[11px] text-slate-500">Вводиться один раз, одразу шифрується. Після збереження адреси гаманця зміняться на похідні від вашого сіду.</div>
            </div>
            {user?.two_fa?.enabled && (
              <div><Label>Код 2FA</Label>
                <Input data-testid="seed-otp" value={seedForm.otp} onChange={(e) => setSeedForm({ ...seedForm, otp: e.target.value })} className="rounded-xl mt-1 max-w-xs" placeholder="123 456" inputMode="numeric" maxLength={6} /></div>
            )}
            <div className="flex gap-2 justify-end">
              <Button size="sm" variant="ghost" className="rounded-full" onClick={() => setSeedForm({ mnemonic: "", otp: "", open: false })}>Скасувати</Button>
              <Button data-testid="seed-save-btn" size="sm" className="rounded-full bg-emerald-600 hover:bg-emerald-700 px-6" onClick={saveSeed} disabled={seedBusy || !seedForm.mnemonic.trim()}>
                {seedBusy ? "Шифрування…" : "Зберегти (зашифрувати)"}
              </Button>
            </div>
          </div>
        )}
      </div>

      {loading ? <div className="text-slate-400">Читаємо баланси on-chain…</div> : data && (
        <>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="rounded-2xl border border-slate-100 p-4">
              <div className="text-xs text-slate-500">EVM адреса (ETH/BSC/Polygon/Arbitrum)</div>
              <div className="mt-1 flex items-center gap-2"><code className="flex-1 truncate text-xs">{data.evm_address}</code>
                <Button size="icon" variant="ghost" onClick={() => copy(data.evm_address)}><Copy className="h-4 w-4" /></Button></div>
            </div>
            <div className="rounded-2xl border border-slate-100 p-4">
              <div className="text-xs text-slate-500">TRON адреса</div>
              <div className="mt-1 flex items-center gap-2"><code className="flex-1 truncate text-xs">{data.tron_address}</code>
                <Button size="icon" variant="ghost" onClick={() => copy(data.tron_address)}><Copy className="h-4 w-4" /></Button></div>
            </div>
          </div>

          <div className="rounded-2xl border border-slate-100 p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="text-lg font-bold text-slate-900">Баланси on-chain <span className="text-sm text-slate-400 font-normal">≈ ${data.total_usd}</span></div>
              <Button size="sm" variant="outline" onClick={load} className="rounded-full"><RefreshCw className="mr-1 h-3.5 w-3.5" />Оновити</Button>
            </div>
            <div className="space-y-2">
              {(data.balances || []).map((b, i) => (
                <div key={i} data-testid={`hot-bal-${b.symbol}-${b.chain}`} className="flex items-center justify-between rounded-xl border border-slate-100 p-3">
                  <div className="flex items-center gap-3">
                    <CoinIcon iso={b.symbol} size={30} />
                    <div>
                      <div className="font-semibold text-slate-900">{Number(b.amount).toLocaleString("en-US", { maximumFractionDigits: 8 })} {b.symbol}</div>
                      <div className="text-xs text-slate-500 capitalize">{b.chain} · {b.kind}</div>
                    </div>
                  </div>
                  <div className="text-sm text-slate-500">${b.usd}</div>
                </div>
              ))}
              {(data.balances || []).length === 0 && <div className="py-6 text-center text-sm text-slate-400">Гаманець порожній (кошти з'являться після депозитів).</div>}
            </div>
          </div>

          <div className="rounded-2xl border border-emerald-100 p-5 space-y-3">
            <div className="text-lg font-bold text-slate-900 flex items-center gap-2"><Send className="h-4 w-4 text-emerald-600" />Вивести з гарячого гаманця</div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div><Label>Мережа</Label>
                <Select value={form.chain} onValueChange={(v) => setForm({ ...form, chain: v })}>
                  <SelectTrigger data-testid="hot-chain" className="rounded-xl mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white border border-slate-200">
                    {["ethereum", "bsc", "polygon", "arbitrum"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Валюта</Label>
                <Select value={form.iso} onValueChange={(v) => setForm({ ...form, iso: v })}>
                  <SelectTrigger data-testid="hot-iso" className="rounded-xl mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white border border-slate-200">
                    {["USDT", "USDC", "ETH", "BNB", "MATIC"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div><Label>Адреса отримувача</Label><Input data-testid="hot-to" value={form.to_address} onChange={(e) => setForm({ ...form, to_address: e.target.value })} className="rounded-xl mt-1 font-mono text-xs" placeholder="0x…" /></div>
            <div><Label>Сума</Label><Input data-testid="hot-amount" type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="rounded-xl mt-1" placeholder="0.00" /></div>
            {user?.two_fa?.enabled && (
              <div><Label className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-emerald-600" />Код 2FA</Label>
                <Input data-testid="hot-otp" value={form.otp} onChange={(e) => setForm({ ...form, otp: e.target.value })} className="rounded-xl mt-1 max-w-xs" placeholder="123 456" inputMode="numeric" maxLength={6} /></div>
            )}
            <div className="flex justify-end">
              <Button data-testid="hot-withdraw-btn" onClick={withdraw} disabled={busy || !form.to_address || !form.amount} className="rounded-full bg-gradient-to-r from-emerald-600 to-emerald-600 px-8 hover:opacity-95">
                {busy ? "Відправка…" : "Вивести кошти"}
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
